import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import type { ProfileListItem } from '../types/api'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import Modal from '../components/ui/Modal'
import FileUpload from '../components/ui/FileUpload'

type BasicForm = {
  name: string
  email: string
  phone: string
  telegram: string
  location: string
  summary: string
  skills: string
  linkedin: string
  github: string
}

const EMPTY_FORM: BasicForm = {
  name: '', email: '', phone: '', telegram: '',
  location: '', summary: '', skills: '', linkedin: '', github: '',
}

export default function Applicants() {
  const navigate = useNavigate()
  const [profiles, setProfiles] = useState<ProfileListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<BasicForm>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const load = () =>
    api.profiles.list().then((r) => setProfiles(r.data)).catch((e) => setError(e.message)).finally(() => setLoading(false))

  useEffect(() => { load() }, [])

  const handleUpload = async (file: File) => {
    await api.profiles.uploadFile(file)
    setShowUpload(false)
    load()
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setFormError('Name is required'); return }
    setFormError(null)
    setSubmitting(true)
    try {
      const skills = form.skills.split(',').map((s) => s.trim()).filter(Boolean)
      await api.profiles.createBasic({
        name: form.name,
        ...(form.email && { email: form.email }),
        ...(form.phone && { phone: form.phone }),
        ...(form.telegram && { telegram: form.telegram }),
        ...(form.location && { location: form.location }),
        ...(form.summary && { summary: form.summary }),
        ...(skills.length && { skills }),
        ...(form.linkedin && { linkedin: form.linkedin }),
        ...(form.github && { github: form.github }),
      })
      setShowCreate(false)
      setForm(EMPTY_FORM)
      load()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create profile')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-text-muted tracking-tactical mb-1">PERSONNEL</p>
          <h1 className="text-2xl">CANDIDATES</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setShowUpload(true)}>UPLOAD CV</Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>ADD CANDIDATE</Button>
        </div>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {loading ? (
        <p className="text-text-muted text-sm">Loading candidates...</p>
      ) : profiles.length === 0 ? (
        <div className="border border-dashed border-border-subtle p-12 text-center">
          <p className="text-text-muted text-sm">No candidates yet. Upload a CV or add manually.</p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-subtle">
              <th className="text-left text-xs text-text-muted tracking-tactical py-3 pr-4">NAME</th>
              <th className="text-left text-xs text-text-muted tracking-tactical py-3 pr-4">SKILLS</th>
              <th className="text-left text-xs text-text-muted tracking-tactical py-3">DEPTH</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/admin/applicants/${p.id}`)}
                className="border-b border-border-subtle cursor-pointer hover:bg-surface-low transition-colors"
              >
                <td className="py-3 pr-4 font-medium">{p.name}</td>
                <td className="py-3 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {p.skills.slice(0, 5).map((s) => (
                      <Badge key={s}>{s}</Badge>
                    ))}
                    {p.skills.length > 5 && (
                      <Badge variant="muted">+{p.skills.length - 5}</Badge>
                    )}
                  </div>
                </td>
                <td className="py-3">
                  <Badge variant={p.interview_depth > 0 ? 'preferred' : 'muted'}>
                    {p.interview_depth}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal isOpen={showUpload} onClose={() => setShowUpload(false)} title="UPLOAD CV FILE">
        <FileUpload
          onUpload={handleUpload}
          label="Upload CV (PDF, TXT, MD, DOC)"
        />
      </Modal>

      <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); setForm(EMPTY_FORM); setFormError(null) }} title="ADD CANDIDATE">
        <form onSubmit={handleCreate} className="space-y-3">
          {formError && <p className="text-red-400 text-xs">{formError}</p>}
          {[
            { key: 'name', label: 'NAME *', placeholder: 'John Doe' },
            { key: 'email', label: 'EMAIL', placeholder: 'john@example.com' },
            { key: 'phone', label: 'PHONE', placeholder: '+358 40 123 4567' },
            { key: 'telegram', label: 'TELEGRAM', placeholder: '@johndoe' },
            { key: 'location', label: 'LOCATION', placeholder: 'Helsinki, Finland' },
            { key: 'linkedin', label: 'LINKEDIN', placeholder: 'https://linkedin.com/in/...' },
            { key: 'github', label: 'GITHUB', placeholder: 'https://github.com/...' },
            { key: 'skills', label: 'SKILLS (comma-separated)', placeholder: 'TypeScript, React, Node.js' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-xs text-text-muted tracking-tactical mb-1">{label}</label>
              <input
                type="text"
                value={form[key as keyof BasicForm]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-text-muted tracking-tactical mb-1">SUMMARY</label>
            <textarea
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              placeholder="Brief summary..."
              rows={3}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary resize-none"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={submitting}>{submitting ? 'CREATING...' : 'CREATE CANDIDATE'}</Button>
            <Button variant="ghost" type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_FORM) }}>CANCEL</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
